import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FiPlus, FiEdit2, FiTrash2, FiUpload, FiX, FiChevronDown, FiChevronUp, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import {
  fetchBubbleTeaMenuItems,
  createBubbleTeaMenuItem,
  updateBubbleTeaMenuItem,
  deleteBubbleTeaMenuItem,
  BubbleTeaMenuItemAdmin,
  BubbleTeaMenuItemInput,
  adminBubbleTeaOptionCategories,
  adminBubbleTeaOptionItems,
  BubbleTeaOptionCategory,
  uploadMenuItemImage,
  bulkUploadMenuItemImages,
  BulkImageUploadMapping,
  BulkImageUploadResult,
  adminBubbleTeaMenuItems,
  refreshMenuItemQrCode,
  refreshMenuItemQrCombos,
  fetchMenuItemQrCombos,
  MenuItemQrComboListResult,
  getMenuItemOptions,
  toggleMenuItemOption,
} from '../../api/adminBubbleTeaOptions';
import axiosInstance from '../../api/axios';
import { formatCurrency } from '../../utils/currency';
import { localizeErrorDetail, getErrorDetail } from '../../utils/errorDetail';
import { trimShortCode } from '../../utils/trimShortCode';
import QrCodeField from '../../components/Admin/QrCodeField';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace('/api', '');
const resolveImageUrl = (url: string | null | undefined): string =>
  !url ? '' : (url.startsWith('data:') || url.startsWith('http')) ? url : `${API_ORIGIN}${url}`;

// adminBubbleTeaOptions.ts's bulkUploadImages timeout formula
// (Math.min(MAX_BULK_UPLOAD_TIMEOUT_MS, Math.max(MIN_BULK_UPLOAD_TIMEOUT_MS, files.length * 15000)))
// stops scaling with batch size once it hits its 600000ms ceiling, which happens
// at exactly 40 files (40 * 15000 = 600000). Above that count the timeout can no
// longer grow to cover the extra files, so cap the batch client-side instead of
// changing the timeout formula (out of scope for this fix).
const MAX_BULK_UPLOAD_FILE_COUNT = 40;

// ─── Styled Components ────────────────────────────────────────────────────────

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1C1511;
  font-family: Georgia, serif;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #D4A017;
  color: #1C1511;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(212, 160, 23, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const IconButton = styled.button<{ variant?: 'edit' | 'delete' }>`
  padding: 8px;
  background: ${props => props.variant === 'delete' ? '#fee2e2' : 'rgba(212, 160, 23, 0.15)'};
  color: ${props => props.variant === 'delete' ? '#dc2626' : '#1C1511'};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.variant === 'delete' ? '#fecaca' : 'rgba(212, 160, 23, 0.3)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: #E8DDD0;
  color: #6B5E4E;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #d9cfc4;
  }
`;

const Card = styled.div`
  background: #F5F0E8;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #FFFFFF;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: #1C1511;
  border-bottom: 1px solid #1C1511;
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #1C1511;
  border-bottom: 1px solid #E8DDD0;
  vertical-align: middle;
`;

const Tr = styled.tr`
  transition: background 0.1s ease;

  &:hover {
    background: #ede8df;
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const ActionCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PriceBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  background: rgba(212, 160, 23, 0.2);
  color: #1C1511;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
`;

const CategoryBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  background: rgba(28, 21, 17, 0.1);
  color: #1C1511;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
`;

const AmbiguousBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  background: rgba(217, 119, 6, 0.15);
  color: #92400E;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
`;

const ToggleLabel = styled.label`
  position: relative;
  display: inline-block;
  width: 38px;
  height: 22px;
  cursor: pointer;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    inset: 0;
    background: #e5e7eb;
    border-radius: 22px;
    transition: background 0.2s ease;

    &::before {
      content: '';
      position: absolute;
      width: 16px;
      height: 16px;
      left: 3px;
      top: 3px;
      background: white;
      border-radius: 50%;
      transition: transform 0.2s ease;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    }
  }

  input:checked + span {
    background: #D4A017;

    &::before {
      transform: translateX(16px);
    }
  }

  input:disabled + span {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Thumbnail = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #E8DDD0;
`;

const NoImage = styled.div`
  width: 40px;
  height: 40px;
  background: #FAF8F3;
  border-radius: 6px;
  border: 1px solid #E8DDD0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #6B5E4E;
`;

const EmptyCell = styled.td`
  padding: 40px 16px;
  text-align: center;
  color: #6B5E4E;
  font-size: 14px;
`;

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background: #FAF8F3;
  border-radius: 12px;
  max-width: 520px;
  width: 90%;
  padding: 24px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1C1511;
  font-family: Georgia, serif;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #6B5E4E;
  cursor: pointer;
  line-height: 1;

  &:hover {
    color: #1C1511;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

// Places an EN FormGroup next to its paired ZH (Chinese) FormGroup, e.g. for
// Name / Name (Chinese). Mirrors AdminBubbleTeaOptions.tsx / AdminProducts.tsx's
// FormRow (task-025 story-004-5).
const FormRow = styled.div`
  display: flex;
  gap: 16px;

  ${FormGroup} {
    flex: 1;
    min-width: 0;
  }
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #1C1511;
  margin-bottom: 6px;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #E8DDD0;
  border-radius: 6px;
  font-size: 14px;
  background: #FFFFFF;
  color: #1C1511;

  &:focus {
    outline: none;
    border-color: #D4A017;
    box-shadow: 0 0 0 3px rgba(212, 160, 23, 0.1);
  }
`;

const Textarea = styled.textarea`
  padding: 8px 12px;
  border: 1px solid #E8DDD0;
  border-radius: 6px;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  background: #FFFFFF;
  color: #1C1511;

  &:focus {
    outline: none;
    border-color: #D4A017;
    box-shadow: 0 0 0 3px rgba(212, 160, 23, 0.1);
  }
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #E8DDD0;
  border-radius: 6px;
  font-size: 14px;
  background: #FFFFFF;
  color: #1C1511;

  &:focus {
    outline: none;
    border-color: #D4A017;
    box-shadow: 0 0 0 3px rgba(212, 160, 23, 0.1);
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #1C1511;
  cursor: pointer;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const SubmitButton = styled(Button)`
  margin: 0;
`;

const OptionsSectionToggle = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: none;
  border: none;
  border-top: 1px solid #E8DDD0;
  padding: 0.75rem 0;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  color: #1C1511;
  &:hover { color: #D4A017; }
`;

const OptionsSectionBody = styled.div`
  padding: 8px 0 4px;
`;

const CategoryBlock = styled.div`
  margin-bottom: 16px;
`;

const CategoryHeader = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: #1C1511;
  margin: 0 0 6px;
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  padding: 3px 0;
  border-bottom: 1px solid #E8DDD0;
`;

const AddItemRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  margin-top: 8px;
`;

const SmallInput = styled.input`
  flex: 1;
  padding: 5px 8px;
  border: 1px solid #E8DDD0;
  border-radius: 5px;
  font-size: 13px;
  background: #FFFFFF;
  color: #1C1511;
`;

const SmallButton = styled.button`
  padding: 5px 10px;
  background: #D4A017;
  color: #1C1511;
  border: none;
  border-radius: 5px;
  font-size: 13px;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ImageUploadArea = styled.div`
  border: 2px dashed #E8DDD0;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  background: transparent;
  margin-bottom: 8px;
`;

const FileInputHidden = styled.input`
  display: none;
`;

const UploadButtonLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #E8DDD0;
  color: #6B5E4E;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    background: #d9cfc4;
    color: #1C1511;
  }
`;

const ImagePreviewWrap = styled.div`
  position: relative;
  display: inline-block;
  margin-top: 8px;
`;

const PreviewImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #E8DDD0;
`;

const RemoveImageBtn = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  background: rgba(0,0,0,0.6);
  color: white;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;

  &:hover {
    background: rgba(0,0,0,0.85);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
`;

const Spinner = styled.div`
  border: 3px solid #E8DDD0;
  border-top: 3px solid #D4A017;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #991b1b;
  border-radius: 8px;
  padding: 16px 20px;
  font-size: 14px;
  margin-bottom: 20px;
`;

// Deliberately separate section (own container, own border, own red-outline
// button style) from the QrCodeField/qr_enabled block above -- this is a
// distinct admin action (rotating task-032's per-combo drink-machine QR
// tokens) that must never be visually or functionally merged with task-020's
// customer-facing "Enable QR Code" field/its own amber Refresh QR Code button.
const ComboQrSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
  padding-top: 16px;
  border-top: 1px dashed #E8DDD0;
`;

const ComboQrHint = styled.p`
  font-size: 13px;
  color: #6B5E4E;
  margin: 0;
`;

const ComboQrRefreshButton = styled.button`
  background: #FFFFFF;
  color: #991b1b;
  border: 1px solid #991b1b;
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  align-self: flex-start;

  &:hover:not(:disabled) {
    background: #fee2e2;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ComboQrSuccessMessage = styled.p`
  font-size: 13px;
  color: #065f46;
  margin: 0;
`;

// Read-only, opt-in list of every currently-live drink-machine combo QR code
// for this menu item (task-037), gated behind ComboQrToggleButton so opening
// the edit modal never fires a network request on its own. Fully separate
// from the mutating ComboQrRefreshButton/handleRefreshComboQrCodes above --
// this section never rotates tokens itself, it only reads.
const ComboQrToggleButton = styled.button`
  background: #FFFFFF;
  color: #6B5E4E;
  border: 1px solid #E8DDD0;
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  align-self: flex-start;

  &:hover:not(:disabled) {
    background: #F5EFE6;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Horizontally-scrollable strip, mirroring QuickBitesMenu.tsx's QBCategoryBar
// small-screen convention (flex row, overflow-x auto, momentum scrolling on
// iOS) -- see .orchestrator/context.md's Discovered section / task-029.
const ComboQrStrip = styled.div`
  display: flex;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  gap: 12px;
`;

const ComboQrCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  padding: 10px;
  background: #FFFFFF;
  border: 1px solid #E8DDD0;
  border-radius: 8px;
  min-width: 140px;
`;

const ComboQrCardImage = styled.img`
  width: 120px;
  height: 120px;
  object-fit: contain;
`;

const ComboQrCardLabel = styled.p`
  font-size: 12px;
  color: #6B5E4E;
  margin: 0;
  text-align: center;
`;

const BulkFileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
`;

const BulkFileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: #FFFFFF;
  border: 1px solid #E8DDD0;
  border-radius: 6px;
`;

const BulkFileName = styled.span`
  flex: 1;
  font-size: 13px;
  color: #1C1511;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BulkResultRow = styled.div<{ success: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 13px;
  background: ${props => props.success ? '#dcfce7' : '#fee2e2'};
  color: ${props => props.success ? '#166534' : '#991b1b'};

  span:last-child {
    margin-left: auto;
    text-align: right;
  }
`;

// ─── Form state ───────────────────────────────────────────────────────────────

interface MenuFormState {
  name: string;
  name_zh: string;
  description: string;
  description_zh: string;
  price: number;
  image_url: string;
  available: boolean;
  menu_category: string;
  qr_enabled: boolean;
  qr_token?: string | null;
  product_code: string;
}

// Bubble Tea menu items default qr_enabled to TRUE for newly-created items,
// matching the backend default (Depends(get_admin_user) create_bubble_tea_menu_item
// mints a token on creation when qr_enabled is not explicitly false) -- this is
// the opposite of AdminProducts.tsx's Custom Cakes default (false), per J39/J38.
const BLANK_FORM: MenuFormState = {
  name: '',
  name_zh: '',
  description: '',
  description_zh: '',
  price: 0,
  image_url: '',
  available: true,
  menu_category: '',
  qr_enabled: true,
  qr_token: null,
  product_code: '',
};

function formFromItem(item: BubbleTeaMenuItemAdmin): MenuFormState {
  return {
    name: item.name,
    name_zh: item.name_zh ?? '',
    description: item.description ?? '',
    description_zh: item.description_zh ?? '',
    price: item.price,
    image_url: item.image_url ?? '',
    available: item.available,
    menu_category: item.menu_category ?? '',
    qr_enabled: item.qr_enabled,
    qr_token: item.qr_token,
    product_code: item.product_code ?? '',
  };
}

// combo_key is built server-side as category=value pairs joined by "|" (see
// backend/app/api/admin_bubbletea_options.py::_build_combo_key), e.g.
// "Tea Type=Green Tea|Sugar Level=Half Sugar". AdminQBOrders.tsx's own
// per-order-line-item rendering only ever displays the raw
// {category_name: value} options dict directly (never a combo_key string),
// so there is no existing shared combo_key-parsing helper to reuse here --
// see .orchestrator/context.md's Discovered section for this note. Splits
// on "|" for each pair, then splits each pair on the FIRST "=" only
// (partition semantics, via indexOf/slice) since a category name or value
// could theoretically contain "=" itself, and renders "Category: Value"
// pairs joined by ", ".
export const formatComboKeyLabel = (comboKey: string): string =>
  comboKey
    .split('|')
    .map(segment => {
      const eqIdx = segment.indexOf('=');
      if (eqIdx === -1) return segment;
      return `${segment.slice(0, eqIdx)}: ${segment.slice(eqIdx + 1)}`;
    })
    .join(', ');

// ─── Main component ───────────────────────────────────────────────────────────

const AdminQBMenu: React.FC = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<BubbleTeaMenuItemAdmin | null>(null);
  const [form, setForm] = useState<MenuFormState>(BLANK_FORM);
  const [error, setError] = useState<string | null>(null);
  const [qrRefreshError, setQrRefreshError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sessionIdRef = useRef(0);
  const bulkSessionIdRef = useRef(0);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [newItemState, setNewItemState] = useState<Record<number, { name: string; extra_price: number }>>({});
  const [editItemState, setEditItemState] = useState<
    Record<number, { name: string; extra_price: number } | null>
  >({});
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkFiles, setBulkFiles] = useState<File[]>([]);
  const [bulkMapping, setBulkMapping] = useState<Record<string, number | ''>>({});
  const [bulkResults, setBulkResults] = useState<BulkImageUploadResult[] | null>(null);
  const bulkFileInputRef = useRef<HTMLInputElement>(null);
  const [qrImageSrc, setQrImageSrc] = useState('');
  const [qrImageFailed, setQrImageFailed] = useState(false);
  const [qrRefreshing, setQrRefreshing] = useState(false);
  // Combo QR (drink-machine) refresh state (task-032-impl-8) -- entirely
  // separate from the qrImageSrc/qrImageFailed/qrRefreshing trio above, which
  // belong exclusively to the unrelated task-020 customer-facing QR field.
  const [comboQrRefreshing, setComboQrRefreshing] = useState(false);
  const [comboQrRefreshError, setComboQrRefreshError] = useState<string | null>(null);
  const [comboQrRefreshSuccess, setComboQrRefreshSuccess] = useState(false);
  // Read-only, opt-in combo QR list state (task-037) -- entirely separate
  // from the comboQrRefresh* trio above. comboQrList stays `null` until the
  // first successful fetch, which doubles as the "already loaded" flag so
  // toggling off then back on does not re-fetch.
  const [comboQrListShown, setComboQrListShown] = useState(false);
  const [comboQrListLoading, setComboQrListLoading] = useState(false);
  const [comboQrListError, setComboQrListError] = useState<string | null>(null);
  const [comboQrList, setComboQrList] = useState<MenuItemQrComboListResult | null>(null);

  const { data: rawItems, isLoading, isError } = useQuery({
    queryKey: ['qbMenuItems'],
    queryFn: fetchBubbleTeaMenuItems,
  });
  const items = Array.isArray(rawItems) ? rawItems : [];

  const { data: rawCatData } = useQuery<{ categories: { name: string; display_order: number }[] }>({
    queryKey: ['qbCategories'],
    queryFn: async () => {
      const res = await axiosInstance.get<{ categories: { name: string; display_order: number }[] }>('/admin/settings/qb-categories');
      return res.data;
    },
  });
  const categories: { name: string; display_order: number }[] = Array.isArray(rawCatData?.categories) ? rawCatData!.categories : [];

  const { data: rawOptionCats, isLoading: categoriesLoading } = useQuery<BubbleTeaOptionCategory[]>({
    queryKey: ['qbOptionCategories'],
    queryFn: adminBubbleTeaOptionCategories.getAll,
    enabled: !!modalOpen && optionsOpen,
  });
  const optionCategories = Array.isArray(rawOptionCats) ? rawOptionCats : [];

  // Per-product option availability (task-028 Sprint 5). Only meaningful for an
  // existing saved product (editingItem?.menu_item_id) -- a not-yet-saved new
  // product has no menu_item_id to fetch/toggle against. Scoped by menu_item_id
  // in the query key so switching between products' edit modals never shows
  // stale availability from a previously-open product.
  const menuItemIdForOptions = editingItem?.menu_item_id;
  const { data: rawMenuItemOptionIds } = useQuery<number[]>({
    queryKey: ['qbMenuItemOptions', menuItemIdForOptions],
    queryFn: () => getMenuItemOptions(menuItemIdForOptions as number),
    enabled: !!modalOpen && optionsOpen && menuItemIdForOptions != null,
  });
  const menuItemOptionIds = Array.isArray(rawMenuItemOptionIds) ? rawMenuItemOptionIds : [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['qbMenuItems'] });

  const createMutation = useMutation({
    mutationFn: ({ data }: { data: Partial<BubbleTeaMenuItemInput>; sessionId: number }) =>
      createBubbleTeaMenuItem(data),
    onSuccess: (_data, variables) => {
      invalidate();
      if (variables.sessionId === sessionIdRef.current) { closeModal(); }
    },
    onError: (err: any, variables) => {
      if (variables.sessionId === sessionIdRef.current) {
        setSaveError(localizeErrorDetail(getErrorDetail(err), t) || t('admin.qbMenu.genericError'));
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<BubbleTeaMenuItemInput>; sessionId: number }) =>
      updateBubbleTeaMenuItem(id, data),
    onSuccess: (_data, variables) => {
      invalidate();
      // A save may have changed menu_category, which the backend now
      // resets/reseeds bubble_tea_menu_item_options for (task-028 s8
      // fix-2) -- always invalidate this product's options query too, so a
      // still-open (or reopened) options panel doesn't show stale
      // pre-move availability. Cheap no-op when the query key isn't
      // currently mounted/cached.
      queryClient.invalidateQueries({ queryKey: ['qbMenuItemOptions', variables.id] });
      if (variables.sessionId === sessionIdRef.current) { closeModal(); }
    },
    onError: (err: any, variables) => {
      if (variables.sessionId === sessionIdRef.current) {
        setSaveError(localizeErrorDetail(getErrorDetail(err), t) || t('admin.qbMenu.genericError'));
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteBubbleTeaMenuItem(id),
    onSuccess: () => { setDeleteError(null); invalidate(); },
    onError: (err: any) => {
      setDeleteError(localizeErrorDetail(getErrorDetail(err), t) || t('admin.qbMenu.deleteItemErrorFallback'));
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, available }: { id: number; available: boolean }) =>
      updateBubbleTeaMenuItem(id, { available }),
    onSuccess: invalidate,
  });

  const uploadImageMutation = useMutation({
    mutationFn: ({ id, file }: { id: number; file: File; sessionId: number }) => uploadMenuItemImage(id, file),
    onSuccess: (updated: BubbleTeaMenuItemAdmin, variables) => {
      invalidate();
      if (variables.sessionId === sessionIdRef.current) {
        setForm(prev => ({ ...prev, image_url: updated.image_url ?? '' }));
      }
    },
    onError: (err: any, variables) => {
      if (variables.sessionId === sessionIdRef.current) {
        setError(localizeErrorDetail(getErrorDetail(err), t) || t('admin.qbMenu.imageUploadFailedError'));
      }
    },
  });

  const bulkUploadMutation = useMutation({
    mutationFn: ({ files, mapping }: { files: File[]; mapping: BulkImageUploadMapping; sessionId: number }) =>
      bulkUploadMenuItemImages(files, mapping),
    onSuccess: (results: BulkImageUploadResult[], variables) => {
      invalidate();
      if (variables.sessionId === bulkSessionIdRef.current) {
        setBulkResults(results);
      }
    },
    // task-058 fix_review: the backend keeps processing files to completion
    // even after the client aborts (e.g. a per-call timeout on a large
    // batch), so files already committed server-side before the failure
    // remain uploaded/DB-mapped. Invalidate the menu-items query on failure
    // too (mirroring onSuccess above) so the underlying list/thumbnails
    // reflect whatever the backend actually finished, rather than leaving
    // stale data visible alongside a generic failure banner.
    onError: () => { invalidate(); },
  });

  const addItemMutation = useMutation({
    mutationFn: ({ categoryId, data }: { categoryId: number; data: any }) =>
      adminBubbleTeaOptionItems.create(categoryId, data),
    onSuccess: (_data: any, { categoryId }: { categoryId: number }) => {
      queryClient.invalidateQueries({ queryKey: ['qbOptionCategories'] });
      setNewItemState(s => { const next = { ...s }; delete next[categoryId]; return next; });
    },
  });

  const updateItemMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { name: string; extra_price: number } }) =>
      adminBubbleTeaOptionItems.update(id, data),
    onSuccess: (_data: any, { id }: { id: number }) => {
      queryClient.invalidateQueries({ queryKey: ['qbOptionCategories'] });
      setEditItemState(s => { const next = { ...s }; delete next[id]; return next; });
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: (id: number) => adminBubbleTeaOptionItems.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qbOptionCategories'] });
    },
  });

  const toggleMenuItemOptionMutation = useMutation({
    mutationFn: ({ menuItemId, optionItemId }: { menuItemId: number; optionItemId: number }) =>
      toggleMenuItemOption(menuItemId, optionItemId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['qbMenuItemOptions', variables.menuItemId] });
    },
  });

  const openAdd = () => {
    sessionIdRef.current += 1;
    setDeleteError(null);
    setEditingItem(null);
    setForm(BLANK_FORM);
    setModalOpen(true);
  };

  const openEdit = (item: BubbleTeaMenuItemAdmin) => {
    sessionIdRef.current += 1;
    setDeleteError(null);
    setEditingItem(item);
    setForm(formFromItem(item));
    setModalOpen(true);
  };

  const closeModal = () => {
    sessionIdRef.current += 1;
    setModalOpen(false);
    setEditingItem(null);
    setForm(BLANK_FORM);
    setOptionsOpen(false);
    setNewItemState({});
    setEditItemState({});
    setError(null);
    setSaveError(null);
    setQrRefreshError(null);
    setQrImageFailed(false);
    setQrRefreshing(false);
    setComboQrRefreshing(false);
    setComboQrRefreshError(null);
    setComboQrRefreshSuccess(false);
    setComboQrListShown(false);
    setComboQrListLoading(false);
    setComboQrListError(null);
    setComboQrList(null);
  };

  // GET /admin/bubble-tea-options/menu-items/{id}/qr-image is admin-gated via
  // a Bearer token header (Depends(get_admin_user)), so a plain <img src> would
  // 401 -- there's no cookie-based session fallback. Fetch the PNG through the
  // authenticated axios client as a blob and hand QrCodeField an object URL,
  // mirroring AdminProducts.tsx's task-020-s4-1 pattern exactly (see
  // .orchestrator/context.md's Discovered section).
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    const loadQrImage = async () => {
      if (modalOpen && editingItem?.menu_item_id && form.qr_enabled && form.qr_token) {
        setQrImageFailed(false);
        setQrImageSrc('');
        try {
          const blob = await adminBubbleTeaMenuItems.getQrImage(editingItem.menu_item_id);
          if (cancelled) return;
          objectUrl = URL.createObjectURL(blob);
          setQrImageSrc(objectUrl);
          setError(null);
        } catch (err) {
          console.error('Failed to load QR code image:', err);
          if (!cancelled) {
            setQrImageSrc('');
            setError(t('admin.qbMenu.qrImageLoadFailedError'));
            setQrImageFailed(true);
          }
        }
      } else {
        setQrImageSrc('');
        setQrImageFailed(false);
      }
    };

    loadQrImage();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [modalOpen, editingItem?.menu_item_id, form.qr_enabled, form.qr_token]);

  const handleRefreshQrCode = async () => {
    if (!editingItem?.menu_item_id || qrRefreshing) return;
    setQrRefreshing(true);
    setQrRefreshError(null);
    try {
      const updated = await refreshMenuItemQrCode(editingItem.menu_item_id);
      // Update just the token so the effect above reloads the image, without
      // a full menu-item list re-fetch.
      setForm(prev => ({ ...prev, qr_token: updated.qr_token }));
    } catch (err) {
      console.error('Failed to refresh QR code:', err);
      setQrRefreshError(t('admin.qbMenu.qrRefreshFailedError'));
      try {
        const currentItems = await fetchBubbleTeaMenuItems();
        const current = currentItems.find((i: any) => i.menu_item_id === editingItem?.menu_item_id);
        if (current) {
          setForm(prev => ({ ...prev, qr_token: current.qr_token }));
        } else {
          setQrImageSrc('');
        }
      } catch (reconcileErr) {
        console.error('Failed to reconcile QR code state after refresh error:', reconcileErr);
        setQrImageSrc('');
      }
    } finally {
      setQrRefreshing(false);
    }
  };

  // Rotates every existing drink-machine combo QR token for this menu item
  // (task-032) via POST .../qr-combos/refresh. Fully independent of
  // handleRefreshQrCode/QrCodeField above (task-020's customer-facing
  // qr_enabled/qr_token field): does not read or write form.qr_token, does
  // not touch qrImageSrc/qrImageFailed/qrRefreshing, and the backend scopes
  // the rotation to this single menu_item_id so no other menu item's combos
  // are affected. Also bails out while `comboQrListLoading` is true (in
  // addition to the existing `comboQrRefreshing` re-entrancy guard): the
  // toggle's own initial list fetch (handleToggleComboQrList) writes to the
  // same comboQrList/comboQrListError state that this function's own reload
  // below writes to, and neither writer distinguishes overlapping requests
  // beyond the coarse per-modal-session sessionIdRef, so letting both fetches
  // run concurrently could let a stale response overwrite a fresher one.
  const handleRefreshComboQrCodes = async () => {
    if (!editingItem?.menu_item_id || comboQrRefreshing || comboQrListLoading) return;
    const confirmed = window.confirm(t('admin.qbMenu.refreshComboQrConfirm'));
    if (!confirmed) return;
    setComboQrRefreshing(true);
    setComboQrRefreshError(null);
    setComboQrRefreshSuccess(false);
    try {
      await refreshMenuItemQrCombos(editingItem.menu_item_id);
      setComboQrRefreshSuccess(true);
      // If the read-only list (task-037) is currently shown, re-fetch it via
      // the GET endpoint so the strip reflects the freshly rotated tokens.
      // Deliberately never reuses this POST's own response for the list --
      // that response is unfiltered (includes orphaned/stale combo rows,
      // see context.md's task-032-impl-3 Discovered note), unlike the GET
      // endpoint, which live-filters to only currently-reachable combos.
      if (comboQrListShown) {
        const sessionId = sessionIdRef.current;
        setComboQrListLoading(true);
        setComboQrListError(null);
        try {
          const listResult = await fetchMenuItemQrCombos(editingItem.menu_item_id);
          if (sessionId === sessionIdRef.current) {
            setComboQrList(listResult);
          }
        } catch (listErr) {
          console.error('Failed to reload drink-machine QR codes after refresh:', listErr);
          if (sessionId === sessionIdRef.current) {
            setComboQrListError(t('admin.qbMenu.comboQrListError'));
            setComboQrList(null);
          }
        } finally {
          if (sessionId === sessionIdRef.current) {
            setComboQrListLoading(false);
          }
        }
      }
    } catch (err) {
      console.error('Failed to refresh drink-machine QR codes:', err);
      setComboQrRefreshError(t('admin.qbMenu.refreshComboQrFailedError'));
    } finally {
      setComboQrRefreshing(false);
    }
  };

  // Toggles the read-only combo QR list (task-037). Only fetches on the
  // shown transition, and only when no data is loaded yet (comboQrList is
  // still `null`) -- opening the edit modal must never fire a network
  // request for this feature on its own; the fetch only happens once the
  // admin explicitly clicks this toggle.
  const handleToggleComboQrList = async () => {
    if (!editingItem?.menu_item_id) return;
    const nextShown = !comboQrListShown;
    setComboQrListShown(nextShown);
    if (!nextShown || comboQrList || comboQrListLoading) return;
    const sessionId = sessionIdRef.current;
    setComboQrListLoading(true);
    setComboQrListError(null);
    try {
      const result = await fetchMenuItemQrCombos(editingItem.menu_item_id);
      if (sessionId === sessionIdRef.current) {
        setComboQrList(result);
      }
    } catch (err) {
      console.error('Failed to load drink-machine QR codes:', err);
      if (sessionId === sessionIdRef.current) {
        setComboQrListError(t('admin.qbMenu.comboQrListError'));
      }
    } finally {
      if (sessionId === sessionIdRef.current) {
        setComboQrListLoading(false);
      }
    }
  };

  // Case-insensitive substring match of the file's base name (extension
  // stripped, underscores/dashes normalized to spaces) against menu item
  // names, either direction, so "taro-milk-tea.png" matches "Taro Milk Tea"
  // and "milk-tea.png" matches an item simply named "Milk Tea".
  //
  // Returns ALL matching items (not just the first) so callers can detect
  // ambiguity when overlapping product names (e.g. "Milk Tea" and "Taro
  // Milk Tea") both match the same filename.
  const getMatchingMenuItems = (filename: string) => {
    const base = filename
      .replace(/\.[^/.]+$/, '')
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .trim();
    if (!base) return [];
    return items.filter(item => {
      const name = item.name.toLowerCase();
      return name.length > 0 && (base.includes(name) || name.includes(base));
    });
  };

  const suggestMenuItemId = (filename: string): number | '' => {
    return getMatchingMenuItems(filename)[0]?.menu_item_id ?? '';
  };

  const openBulkModal = () => {
    bulkSessionIdRef.current += 1;
    setBulkModalOpen(true);
    setBulkFiles([]);
    setBulkMapping({});
    setBulkResults(null);
  };

  const closeBulkModal = () => {
    bulkSessionIdRef.current += 1;
    setBulkModalOpen(false);
    setBulkFiles([]);
    setBulkMapping({});
    setBulkResults(null);
    if (bulkFileInputRef.current) bulkFileInputRef.current.value = '';
  };

  const handleBulkFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (selected.length === 0) return;
    setBulkFiles(selected);
    setBulkResults(null);
    if (selected.length <= MAX_BULK_UPLOAD_FILE_COUNT) {
      const mapping: Record<string, number | ''> = {};
      selected.forEach(file => {
        mapping[file.name] = suggestMenuItemId(file.name);
      });
      setBulkMapping(mapping);
    } else {
      setBulkMapping({});
    }
  };

  const handleBulkMappingChange = (filename: string, value: string) => {
    setBulkMapping(prev => ({ ...prev, [filename]: value === '' ? '' : Number(value) }));
  };

  const handleBulkSubmit = () => {
    const mapping: BulkImageUploadMapping = {};
    bulkFiles.forEach(file => {
      const itemId = bulkMapping[file.name];
      if (itemId !== '' && itemId != null) {
        mapping[file.name] = itemId;
      }
    });
    const sessionId = bulkSessionIdRef.current;
    bulkUploadMutation.mutate({ files: bulkFiles, mapping, sessionId });
  };

  const processFile = (file: File) => {
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setForm(prev => ({ ...prev, image_url: reader.result as string }));
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (editingItem) {
        if (file.type.startsWith('image/')) {
          setError(null);
          const sessionId = sessionIdRef.current;
          uploadImageMutation.mutate({ id: editingItem.menu_item_id, file, sessionId });
        } else {
          setError(t('admin.qbMenu.selectImageFileError'));
        }
      } else {
        processFile(file);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaveError(null);
    let imageUrl = form.image_url.trim() || null;
    if (imageUrl && imageUrl.startsWith('data:')) {
      try {
        const res = await axiosInstance.post<{ url: string }>('/admin/upload-base64-strict', { image: imageUrl });
        imageUrl = res.data.url;
      } catch (err) {
        setError(t('admin.qbMenu.imageUploadFailedError'));
        return;
      }
    }
    const payload: Partial<BubbleTeaMenuItemInput> = {
      name: form.name,
      name_zh: form.name_zh || null,
      description: form.description || null,
      description_zh: form.description_zh || null,
      price: form.price,
      image_url: imageUrl,
      available: form.available,
      menu_category: form.menu_category.trim() || null,
      qr_enabled: form.qr_enabled,
    };
    // Blank => omit so the server auto-generates (create) or keeps the
    // existing code (update).
    const productCode = trimShortCode(form.product_code);
    if (productCode) payload.product_code = productCode;
    const sessionId = sessionIdRef.current;
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.menu_item_id, data: payload, sessionId });
    } else {
      createMutation.mutate({ data: payload, sessionId });
    }
  };

  const handleDelete = (item: BubbleTeaMenuItemAdmin) => {
    if (window.confirm(t('admin.qbMenu.deleteConfirm', { name: item.name }))) {
      setDeleteError(null);
      deleteMutation.mutate(item.menu_item_id);
    }
  };

  const isMutating = createMutation.isPending || updateMutation.isPending || uploadImageMutation.isPending;

  // Only the bulk upload belonging to the currently-open session should lock the
  // file picker — a stale in-flight request from a closed/reopened session (see the
  // bulkSessionIdRef guard above) must not block picking a fresh batch of files.
  const bulkUploadPendingForCurrentSession =
    bulkUploadMutation.isPending && bulkUploadMutation.variables?.sessionId === bulkSessionIdRef.current;

  // Duplicate filenames within a single bulk selection collide in bulkMapping
  // (which is keyed by filename), silently dropping the mapping for all but one
  // of the duplicates. Detect and surface this before Submit is allowed.
  const duplicateBulkFilenames = Array.from(
    new Set(bulkFiles.map(f => f.name).filter((name, idx, arr) => arr.indexOf(name) !== idx))
  );
  const hasDuplicateBulkFilenames = duplicateBulkFilenames.length > 0;

  // Two distinct filenames can each be mapped (via auto-suggest or the manual dropdown)
  // to the same menu_item_id. Nothing else prevents this, and both uploads would commit
  // independently — the second silently overwriting the first item's image_url. Detect
  // and surface this before Submit is allowed, mirroring the duplicate-filename guard above.
  const duplicateBulkMenuItemIds = Array.from(
    new Set(
      Object.values(bulkMapping)
        .filter((id): id is number => id !== '' && id != null)
        .filter((id, idx, arr) => arr.indexOf(id) !== idx)
    )
  );
  const hasDuplicateBulkMenuItemIds = duplicateBulkMenuItemIds.length > 0;

  // Above MAX_BULK_UPLOAD_FILE_COUNT files, adminBubbleTeaOptions.ts's per-call
  // timeout can no longer scale with batch size (see the constant's definition
  // above). Block Submit client-side rather than letting an oversized batch fire
  // a request that may hit the timeout ceiling mid-upload.
  const hasTooManyBulkFiles = bulkFiles.length > MAX_BULK_UPLOAD_FILE_COUNT;

  // A filename can match more than one menu item's name (e.g. "taro-milk-tea.png"
  // matches both "Milk Tea" and "Taro Milk Tea"). The auto-suggest still picks the
  // first match with no indication anything was ambiguous — flag these rows so an
  // admin doing a rushed bulk upload knows to double-check the auto-suggested mapping.
  // This is advisory only: it does not change suggestMenuItemId's return value or
  // block Submit. The scan is O(files x menu items), so it is gated on
  // hasTooManyBulkFiles: over the cap no per-file rows render (see below), so
  // there is nothing to badge and the scan is skipped.
  const ambiguousBulkFilenames = hasTooManyBulkFiles
    ? new Set<string>()
    : new Set(bulkFiles.filter(f => getMatchingMenuItems(f.name).length > 1).map(f => f.name));

  // task-058 fix_review: an axios client-side timeout rejection has
  // error.code === 'ECONNABORTED' and no .response property at all --
  // distinguish it from any other bulk-upload failure shape so the banner
  // can warn about partial-success state (see the timeout-specific copy
  // below) instead of the generic failure message.
  const isLikelyBulkUploadTimeout = (err: unknown): boolean =>
    (err as { code?: string } | null)?.code === 'ECONNABORTED';

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  if (isError) {
    return (
      <Container>
        <Header>
          <Title>{t('admin.qbMenu.title')}</Title>
        </Header>
        <ErrorMessage>{t('admin.qbMenu.loadError')}</ErrorMessage>
      </Container>
    );
  }

  return (
    <>
      <Container>
        <Header>
          <Title>{t('admin.qbMenu.title')}</Title>
          <div style={{ display: 'flex', gap: 8 }}>
            <CancelButton
              type="button"
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              onClick={openBulkModal}
            >
              <FiUpload size={14} />
              {t('admin.qbMenu.bulkUploadButton')}
            </CancelButton>
            <Button onClick={openAdd}>
              <FiPlus size={14} />
              {t('admin.qbMenu.addButton')}
            </Button>
          </div>
        </Header>

        {deleteError && (
          <p style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{deleteError}</p>
        )}

        <Card>
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>{t('admin.qbMenu.imageHeader')}</Th>
                  <Th>{t('admin.qbMenu.nameHeader')}</Th>
                  <Th>{t('admin.qbMenu.descriptionHeader')}</Th>
                  <Th>{t('admin.qbMenu.priceHeader')}</Th>
                  <Th>{t('admin.qbMenu.categoryHeader')}</Th>
                  <Th>{t('admin.qbMenu.availableHeader')}</Th>
                  <Th>{t('admin.qbMenu.actionsHeader')}</Th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <EmptyCell colSpan={7}>
                      {t('admin.qbMenu.emptyState')}
                    </EmptyCell>
                  </tr>
                ) : (
                  items.map(item => (
                    <Tr key={item.menu_item_id}>
                      <Td>
                        {item.image_url
                          ? <Thumbnail src={resolveImageUrl(item.image_url)} alt={item.name} />
                          : <NoImage>{t('admin.qbMenu.noImageLabel')}</NoImage>
                        }
                      </Td>
                      <Td style={{ fontWeight: 600 }}>{item.name}</Td>
                      <Td style={{ color: '#6b7280', maxWidth: 220 }}>
                        <span title={item.description ?? ''}>
                          {(item.description ?? '').length > 55
                            ? (item.description ?? '').slice(0, 52) + '…'
                            : (item.description ?? '')}
                        </span>
                      </Td>
                      <Td>
                        <PriceBadge>{formatCurrency(item.price)}</PriceBadge>
                      </Td>
                      <Td>
                        {item.menu_category
                          ? <CategoryBadge>{item.menu_category}</CategoryBadge>
                          : <span style={{ color: '#9ca3af' }}>—</span>
                        }
                      </Td>
                      <Td>
                        <ToggleLabel title={item.available ? t('admin.qbMenu.makeUnavailableTooltip') : t('admin.qbMenu.makeAvailableTooltip')}>
                          <input
                            type="checkbox"
                            checked={item.available}
                            disabled={toggleMutation.isPending}
                            onChange={() => {
                              setDeleteError(null);
                              toggleMutation.mutate({ id: item.menu_item_id, available: !item.available });
                            }}
                          />
                          <span />
                        </ToggleLabel>
                      </Td>
                      <Td>
                        <ActionCell>
                          <IconButton
                            variant="edit"
                            title={t('admin.qbMenu.editTooltip')}
                            onClick={() => openEdit(item)}
                          >
                            <FiEdit2 size={14} />
                          </IconButton>
                          <IconButton
                            variant="delete"
                            title={t('admin.qbMenu.deleteTooltip')}
                            disabled={deleteMutation.isPending}
                            onClick={() => handleDelete(item)}
                          >
                            <FiTrash2 size={14} />
                          </IconButton>
                        </ActionCell>
                      </Td>
                    </Tr>
                  ))
                )}
              </tbody>
            </Table>
          </TableWrapper>
        </Card>
      </Container>

      <Modal isOpen={modalOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {editingItem ? t('admin.qbMenu.modalEditTitle', { name: editingItem.name }) : t('admin.qbMenu.modalAddTitle')}
            </ModalTitle>
            <CloseButton onClick={closeModal}>×</CloseButton>
          </ModalHeader>

          <Form onSubmit={handleSubmit}>
            <FormRow>
              <FormGroup>
                <Label>{t('admin.qbMenu.nameLabel')}</Label>
                <Input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t('admin.qbMenu.namePlaceholder')}
                  required
                  autoFocus
                />
              </FormGroup>

              <FormGroup>
                <Label>{t('admin.qbMenu.nameZhLabel')}</Label>
                <Input
                  type="text"
                  value={form.name_zh}
                  onChange={e => setForm(prev => ({ ...prev, name_zh: e.target.value }))}
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>{t('admin.qbMenu.descriptionLabel')}</Label>
                <Textarea
                  value={form.description}
                  onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder={t('admin.qbMenu.descriptionPlaceholder')}
                />
              </FormGroup>

              <FormGroup>
                <Label>{t('admin.qbMenu.descriptionZhLabel')}</Label>
                <Textarea
                  value={form.description_zh}
                  onChange={e => setForm(prev => ({ ...prev, description_zh: e.target.value }))}
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>{t('admin.qbMenu.priceLabel')}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.price}
                onChange={e =>
                  setForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))
                }
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>{t('admin.qbMenu.productCodeLabel')}</Label>
              <Input
                type="text"
                data-testid="menu-item-product-code"
                value={form.product_code}
                onChange={e => setForm(prev => ({ ...prev, product_code: e.target.value }))}
                placeholder={t('admin.qbMenu.productCodePlaceholder')}
              />
              <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 0' }}>
                {t('admin.qbMenu.productCodeHelp')}
              </p>
            </FormGroup>

            <FormGroup>
              <Label>{t('admin.qbMenu.categoryLabel')}</Label>
              <Select
                value={form.menu_category}
                onChange={e => setForm(prev => ({ ...prev, menu_category: e.target.value }))}
              >
                <option value="">{t('admin.qbMenu.noCategoryOption')}</option>
                {categories.map(cat => (
                  <option key={cat.name} value={cat.name}>{cat.name}</option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>{t('admin.qbMenu.imageLabel')}</Label>
              <ImageUploadArea>
                <FileInputHidden
                  ref={fileInputRef}
                  type="file"
                  id="qbMenuImageUpload"
                  accept="image/*"
                  disabled={uploadImageMutation.isPending}
                  onChange={handleFileChange}
                />
                <UploadButtonLabel
                  htmlFor="qbMenuImageUpload"
                  style={uploadImageMutation.isPending ? { opacity: 0.6, cursor: 'not-allowed', pointerEvents: 'none' } : undefined}
                >
                  <FiUpload size={14} />
                  {uploadImageMutation.isPending ? t('admin.qbMenu.uploadingImageButton') : t('admin.qbMenu.uploadImageButton')}
                </UploadButtonLabel>
              </ImageUploadArea>
              {form.image_url && (
                <ImagePreviewWrap>
                  <PreviewImage
                    src={
                      form.image_url.startsWith('data:') || form.image_url.startsWith('http')
                        ? form.image_url
                        : `${(process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace('/api', '')}${form.image_url}`
                    }
                    alt={t('admin.imagePreviewAlt')}
                  />
                  <RemoveImageBtn
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, image_url: '' }))}
                  >
                    <FiX size={10} />
                  </RemoveImageBtn>
                </ImagePreviewWrap>
              )}
            </FormGroup>

            <FormGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={form.available}
                  onChange={e => setForm(prev => ({ ...prev, available: e.target.checked }))}
                />
                {t('admin.qbMenu.availableCheckboxLabel')}
              </CheckboxLabel>
            </FormGroup>

            <FormGroup>
              <Label>{t('admin.qbMenu.qrCodeLabel')}</Label>
              <QrCodeField
                qrEnabled={form.qr_enabled ?? false}
                qrToken={form.qr_token}
                onQrEnabledChange={(enabled) => setForm(prev => ({ ...prev, qr_enabled: enabled }))}
                qrImageUrl={qrImageSrc}
                onRefresh={handleRefreshQrCode}
                itemActive={form.available}
                isRefreshing={qrRefreshing}
                failed={qrImageFailed}
              />
            </FormGroup>

            {editingItem?.menu_item_id && (
              <ComboQrSection>
                <Label>{t('admin.qbMenu.refreshComboQrSectionLabel')}</Label>
                <ComboQrHint>{t('admin.qbMenu.refreshComboQrHint')}</ComboQrHint>
                <ComboQrRefreshButton
                  type="button"
                  onClick={handleRefreshComboQrCodes}
                  disabled={comboQrRefreshing || comboQrListLoading}
                >
                  {comboQrRefreshing
                    ? t('admin.qbMenu.refreshingComboQrButton')
                    : t('admin.qbMenu.refreshComboQrButton')}
                </ComboQrRefreshButton>
                {comboQrRefreshError && <ErrorMessage>{comboQrRefreshError}</ErrorMessage>}
                {comboQrRefreshSuccess && (
                  <ComboQrSuccessMessage>{t('admin.qbMenu.refreshComboQrSuccessMessage')}</ComboQrSuccessMessage>
                )}

                <ComboQrToggleButton
                  type="button"
                  data-testid="combo-qr-toggle-button"
                  onClick={handleToggleComboQrList}
                  disabled={comboQrListLoading}
                >
                  {comboQrListShown
                    ? t('admin.qbMenu.comboQrHideToggle')
                    : t('admin.qbMenu.comboQrShowToggle')}
                </ComboQrToggleButton>

                {comboQrListShown && (
                  <>
                    {comboQrListLoading && <ComboQrHint>{t('admin.qbMenu.comboQrListLoading')}</ComboQrHint>}
                    {comboQrListError && <ErrorMessage>{comboQrListError}</ErrorMessage>}
                    {!comboQrListLoading && !comboQrListError && comboQrList && (
                      comboQrList.combos.length === 0 ? (
                        <ComboQrHint data-testid="combo-qr-empty-state">
                          {t('admin.qbMenu.comboQrListEmpty')}
                        </ComboQrHint>
                      ) : (
                        <ComboQrStrip data-testid="combo-qr-strip">
                          {comboQrList.combos.map(combo => (
                            <ComboQrCard data-testid="combo-qr-card" key={combo.combo_key}>
                              <ComboQrCardImage src={combo.qr_image} alt={formatComboKeyLabel(combo.combo_key)} />
                              <ComboQrCardLabel>{formatComboKeyLabel(combo.combo_key)}</ComboQrCardLabel>
                            </ComboQrCard>
                          ))}
                        </ComboQrStrip>
                      )
                    )}
                  </>
                )}
              </ComboQrSection>
            )}

            {error && (
              <ErrorMessage>{error}</ErrorMessage>
            )}
            {qrRefreshError && (
              <ErrorMessage>{qrRefreshError}</ErrorMessage>
            )}
            {!error && saveError && (
              <ErrorMessage>{saveError}</ErrorMessage>
            )}

            <OptionsSectionToggle type="button" onClick={() => setOptionsOpen(v => !v)}>
              {t('admin.qbMenu.drinkOptionsToggle')}
              {optionsOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
            </OptionsSectionToggle>
            {optionsOpen && (
              <OptionsSectionBody>
                {categoriesLoading && <p style={{ fontSize: 13, color: '#6b7280' }}>{t('admin.qbMenu.loadingOptions')}</p>}
                {!categoriesLoading && optionCategories.length === 0 && (
                  <p style={{ fontSize: 13, color: '#6b7280' }}>{t('admin.qbMenu.noOptionCategories')}</p>
                )}
                {optionCategories.map((cat: any) => {
                  const activeItems = (cat.items || []).filter((i: any) => i.active).sort((a: any, b: any) => a.sort_order - b.sort_order);
                  return (
                    <CategoryBlock key={cat.id}>
                      <CategoryHeader>
                        {cat.name} <span style={{ color: '#9ca3af', fontWeight: 400 }}>({t(`admin.qbMenu.optionTypeLabels.${cat.option_type}`, { defaultValue: cat.option_type })})</span>
                      </CategoryHeader>
                      {activeItems.map((item: any) => {
                        const isEditing = editItemState[item.id] != null;
                        if (isEditing) {
                          const draft = editItemState[item.id]!;
                          return (
                            <ItemRow key={item.id} style={{ gap: 6, alignItems: 'center' }}>
                              <SmallInput
                                type="text"
                                value={draft.name}
                                onChange={e =>
                                  setEditItemState(s => ({ ...s, [item.id]: { ...draft, name: e.target.value } }))
                                }
                              />
                              <SmallInput
                                type="number"
                                step="0.01"
                                min="0"
                                style={{ maxWidth: 80 }}
                                value={draft.extra_price}
                                onChange={e =>
                                  setEditItemState(s => ({
                                    ...s,
                                    [item.id]: { ...draft, extra_price: parseFloat(e.target.value) || 0 },
                                  }))
                                }
                              />
                              <SmallButton
                                type="button"
                                disabled={updateItemMutation.isPending || !draft.name.trim()}
                                onClick={() =>
                                  updateItemMutation.mutate({
                                    id: item.id,
                                    data: { name: draft.name.trim(), extra_price: draft.extra_price },
                                  })
                                }
                              >
                                {t('admin.qbMenu.saveItemButton')}
                              </SmallButton>
                              <SmallButton
                                type="button"
                                style={{ background: '#e5e7eb', color: '#374151' }}
                                onClick={() =>
                                  setEditItemState(s => { const next = { ...s }; delete next[item.id]; return next; })
                                }
                              >
                                {t('admin.qbMenu.cancelItemButton')}
                              </SmallButton>
                            </ItemRow>
                          );
                        }
                        return (
                          <ItemRow key={item.id}>
                            <span>{item.name}{item.is_default ? ' ★' : ''}</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              {menuItemIdForOptions != null && (
                                <input
                                  type="checkbox"
                                  title={t('admin.qbMenu.optionAvailabilityToggleTooltip')}
                                  aria-label={t('admin.qbMenu.optionAvailabilityToggleTooltip')}
                                  checked={menuItemOptionIds.includes(item.id)}
                                  disabled={toggleMenuItemOptionMutation.isPending}
                                  onChange={() => {
                                    const isCurrentlyChecked = menuItemOptionIds.includes(item.id);
                                    const checkedCountInCategory = activeItems.filter((i: any) =>
                                      menuItemOptionIds.includes(i.id)
                                    ).length;
                                    const isLastCheckedInSingleSelect =
                                      cat.selection_mode === 'single' &&
                                      isCurrentlyChecked &&
                                      checkedCountInCategory === 1;
                                    if (
                                      isLastCheckedInSingleSelect &&
                                      !window.confirm(t('admin.qbMenu.lastOptionUncheckConfirm', { category: cat.name }))
                                    ) {
                                      return;
                                    }
                                    toggleMenuItemOptionMutation.mutate({
                                      menuItemId: menuItemIdForOptions,
                                      optionItemId: item.id,
                                    });
                                  }}
                                />
                              )}
                              +£{(item.extra_price || 0).toFixed(2)}
                              <button
                                type="button"
                                title={t('admin.qbMenu.editItemTooltip')}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#667eea', padding: 2, display: 'flex' }}
                                onClick={() =>
                                  setEditItemState(s => ({
                                    ...s,
                                    [item.id]: { name: item.name, extra_price: item.extra_price },
                                  }))
                                }
                              >
                                <FiEdit2 size={12} />
                              </button>
                              <button
                                type="button"
                                title={t('admin.qbMenu.deleteItemTooltip')}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: 2, display: 'flex' }}
                                disabled={deleteItemMutation.isPending}
                                onClick={() => {
                                  if (window.confirm(t('admin.qbMenu.deleteItemConfirm', { name: item.name }))) {
                                    deleteItemMutation.mutate(item.id);
                                  }
                                }}
                              >
                                <FiTrash2 size={12} />
                              </button>
                            </span>
                          </ItemRow>
                        );
                      })}
                      <AddItemRow>
                        <SmallInput
                          type="text"
                          placeholder={t('admin.qbMenu.newItemNamePlaceholder')}
                          value={newItemState[cat.id]?.name ?? ''}
                          onChange={e => setNewItemState(s => ({ ...s, [cat.id]: { ...s[cat.id], name: e.target.value, extra_price: s[cat.id]?.extra_price ?? 0 } }))}
                        />
                        <SmallInput
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder={t('admin.qbMenu.extraPricePlaceholder')}
                          style={{ maxWidth: 80 }}
                          value={newItemState[cat.id]?.extra_price ?? ''}
                          onChange={e => setNewItemState(s => ({ ...s, [cat.id]: { ...s[cat.id], extra_price: parseFloat(e.target.value) || 0, name: s[cat.id]?.name ?? '' } }))}
                        />
                        <SmallButton
                          type="button"
                          disabled={addItemMutation.isPending || !(newItemState[cat.id]?.name?.trim())}
                          onClick={() => addItemMutation.mutate({
                            categoryId: cat.id,
                            data: {
                              name: newItemState[cat.id]!.name!.trim(),
                              extra_price: newItemState[cat.id]?.extra_price ?? 0,
                              is_default: false,
                              active: true,
                              sort_order: (cat.items?.length || 0) + 1
                            }
                          })}
                        >
                          {t('admin.qbMenu.addItemRowButton')}
                        </SmallButton>
                      </AddItemRow>
                    </CategoryBlock>
                  );
                })}
                {addItemMutation.isError && (
                  <p style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{localizeErrorDetail(getErrorDetail(addItemMutation.error), t) || t('admin.qbMenu.addItemErrorFallback')}</p>
                )}
                {updateItemMutation.isError && (
                  <p style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{localizeErrorDetail(getErrorDetail(updateItemMutation.error), t) || t('admin.qbMenu.updateItemErrorFallback')}</p>
                )}
                {deleteItemMutation.isError && (
                  <p style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{localizeErrorDetail(getErrorDetail(deleteItemMutation.error), t) || t('admin.qbMenu.deleteItemErrorFallback')}</p>
                )}
              </OptionsSectionBody>
            )}

            <ModalActions>
              <CancelButton type="button" onClick={closeModal}>
                {t('admin.qbMenu.cancelButton')}
              </CancelButton>
              <SubmitButton type="submit" disabled={isMutating}>
                {isMutating
                  ? (editingItem ? t('admin.qbMenu.savingButton') : t('admin.qbMenu.addingButton'))
                  : (editingItem ? t('admin.qbMenu.saveChangesButton') : t('admin.qbMenu.addButton'))}
              </SubmitButton>
            </ModalActions>
          </Form>
        </ModalContent>
      </Modal>

      <Modal isOpen={bulkModalOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>{t('admin.qbMenu.bulkModalTitle')}</ModalTitle>
            <CloseButton onClick={closeBulkModal}>×</CloseButton>
          </ModalHeader>

          <FormGroup>
            <Label>{t('admin.qbMenu.selectImagesLabel')}</Label>
            <ImageUploadArea>
              <FileInputHidden
                ref={bulkFileInputRef}
                type="file"
                id="qbBulkImageUpload"
                accept="image/*"
                multiple
                disabled={bulkUploadPendingForCurrentSession}
                onChange={handleBulkFilesChange}
              />
              <UploadButtonLabel
                htmlFor="qbBulkImageUpload"
                style={bulkUploadPendingForCurrentSession ? { opacity: 0.6, cursor: 'not-allowed', pointerEvents: 'none' } : undefined}
              >
                <FiUpload size={14} />
                {bulkUploadPendingForCurrentSession ? t('admin.qbMenu.uploadingImagesButton') : t('admin.qbMenu.chooseImagesButton')}
              </UploadButtonLabel>
            </ImageUploadArea>
          </FormGroup>

          {bulkFiles.length > 0 && !bulkResults && (
            <FormGroup>
              <Label>{t('admin.qbMenu.matchFilesLabel')}</Label>
              {!hasTooManyBulkFiles && (
              <BulkFileList>
                {bulkFiles.map((file, idx) => (
                  <BulkFileRow key={`${file.name}-${idx}`}>
                    <BulkFileName title={file.name}>{file.name}</BulkFileName>
                    {ambiguousBulkFilenames.has(file.name) && (
                      <AmbiguousBadge title={t('admin.qbMenu.ambiguousMatchesTitle', { names: getMatchingMenuItems(file.name).map(i => i.name).join(', ') })}>
                        {t('admin.qbMenu.ambiguousBadge')}
                      </AmbiguousBadge>
                    )}
                    <Select
                      aria-label={t('admin.qbMenu.menuItemForFileAriaLabel', { filename: file.name })}
                      value={bulkMapping[file.name] ?? ''}
                      onChange={e => handleBulkMappingChange(file.name, e.target.value)}
                      style={{ maxWidth: 200 }}
                    >
                      <option value="">{t('admin.qbMenu.selectItemOption')}</option>
                      {items.map(item => (
                        <option key={item.menu_item_id} value={item.menu_item_id}>
                          {item.name}
                        </option>
                      ))}
                    </Select>
                  </BulkFileRow>
                ))}
              </BulkFileList>
              )}
              {hasDuplicateBulkFilenames && (
                <ErrorMessage style={{ marginTop: 10, marginBottom: 0 }}>
                  {t('admin.qbMenu.duplicateFilenamesError', {
                    s: duplicateBulkFilenames.length > 1 ? 's' : '',
                    names: duplicateBulkFilenames.join(', '),
                  })}
                </ErrorMessage>
              )}
              {hasDuplicateBulkMenuItemIds && (
                <ErrorMessage style={{ marginTop: 10, marginBottom: 0 }}>
                  {t('admin.qbMenu.duplicateMenuItemsError', {
                    names: duplicateBulkMenuItemIds
                      .map(id => items.find(item => item.menu_item_id === id)?.name || `#${id}`)
                      .join(', '),
                  })}
                </ErrorMessage>
              )}
              {hasTooManyBulkFiles && (
                <ErrorMessage style={{ marginTop: 10, marginBottom: 0 }}>
                  {t('admin.qbMenu.tooManyBulkFilesError', {
                    count: bulkFiles.length,
                    max: MAX_BULK_UPLOAD_FILE_COUNT,
                  })}
                </ErrorMessage>
              )}
            </FormGroup>
          )}

          {bulkUploadMutation.isError && bulkUploadMutation.variables?.sessionId === bulkSessionIdRef.current && (
            <ErrorMessage>
              {isLikelyBulkUploadTimeout(bulkUploadMutation.error)
                ? t('admin.qbMenu.bulkUploadTimeoutError')
                : t('admin.qbMenu.bulkUploadFailedError')}
            </ErrorMessage>
          )}

          {bulkResults && (
            <FormGroup>
              <Label>{t('admin.qbMenu.resultsLabel')}</Label>
              <BulkFileList>
                {bulkResults.map(result => (
                  <BulkResultRow key={result.filename} success={result.success}>
                    {result.success ? <FiCheckCircle size={14} /> : <FiAlertCircle size={14} />}
                    <span>{result.filename}</span>
                    <span>{result.success ? t('admin.qbMenu.uploadedStatus') : (result.reason || t('admin.qbMenu.failedStatus'))}</span>
                  </BulkResultRow>
                ))}
              </BulkFileList>
            </FormGroup>
          )}

          <ModalActions>
            <CancelButton type="button" onClick={closeBulkModal}>
              {bulkResults ? t('admin.qbMenu.closeButton') : t('admin.qbMenu.cancelButton')}
            </CancelButton>
            {!bulkResults && (
              <SubmitButton
                type="button"
                disabled={bulkFiles.length === 0 || bulkUploadPendingForCurrentSession || hasDuplicateBulkFilenames || hasDuplicateBulkMenuItemIds || hasTooManyBulkFiles}
                onClick={handleBulkSubmit}
              >
                {bulkUploadPendingForCurrentSession ? t('admin.qbMenu.uploadingBulkButton') : t('admin.qbMenu.uploadButton')}
              </SubmitButton>
            )}
          </ModalActions>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AdminQBMenu;
